import { createSlice } from "@reduxjs/toolkit";
import {
  addToCart,
  deleteProduct,
  fetchCartDetails,
  fetchOrders,
  fetchProductDetail,
  fetchProducts,
  fetchRelatedProduct,
  placeOrder,
  sendProductFeedback,
  updateCartDetail,
  updateOrderStatus,
} from "../action/orderAction";
import { pipSaveTransictionId } from "../../utils/pip";

const initialState = {
  isLoading: false,
  products: [],
  productDetail: {},
  cartList: [],
  selectedProduct: {},
  orderList: [],
};

const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action?.payload;
    },
    increaseQuantity: (state, action) => {
      const product = state.products.find((p) => p.id === action.payload);
      if (product) {
        product.cartQuanity += 1;
        state.selectedProduct = product;
      }
    },
    decreaseQuantity: (state, action) => {
      const product = state.products.find((p) => p.id === action.payload);
      if (product && product.cartQuanity > 1) {
        product.cartQuanity -= 1;
        state.selectedProduct = product;
      }
    },
    increaseProductQuantity: (state, action) => {
      if (state.productDetail) {
        // immutably update the cartQuanity
        state.productDetail = {
          ...state.productDetail,
          cartQuanity: state.productDetail.cartQuanity + 1, // no direct mutation
        };
      }
    },
    decreaseProductQuantity: (state, action) => {
      if (state.productDetail && state.productDetail.cartQuanity > 1) {
        // immutably update the cartQuanity
        state.productDetail = {
          ...state.productDetail,
          cartQuanity: state.productDetail.cartQuanity - 1, // no direct mutation
        };
      }
    },
  },
  extraReducers: (builder) => {
    // fetch-orders
    builder.addCase(fetchOrders.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchOrders.fulfilled, (state, action) => {
      const { data, success } = action?.payload ?? {};
      state.orderList = data ?? [];
      state.isLoading = false;
    });
    builder.addCase(fetchOrders.rejected, (state, action) => {
      state.isLoading = false;
    });
    // fetch-products
    builder.addCase(fetchProducts.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchProducts.fulfilled, (state, action) => {
      const { data, success } = action?.payload ?? {};
      state.products = (data ?? []).map((product) => ({
        ...product,
        cartQuanity: 1, // Adding a default key with value 1
      }));
      state.isLoading = false;
    });
    builder.addCase(fetchProducts.rejected, (state, action) => {
      state.isLoading = false;
    });
    // fetch-product-detail
    builder.addCase(fetchProductDetail.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchProductDetail.fulfilled, (state, action) => {
      const { data, success } = action?.payload ?? {};

      // Check if the response is successful and has valid data
      if (success && data) {
        const formattedImages =
          data.product_images?.images?.map((url) => ({
            original: url,
            thumbnail: url,
          })) ?? [];

        state.productDetail = {
          ...data,
          product_images: {
            ...data.product_images,
            images: formattedImages,
          },
          cartQuanity: 1, // Default cart quantity
        };
      } else {
        state.productDetail = null; // Handle cases where data is not returned
      }

      state.isLoading = false; // Set loading to false after completion
    });

    // fetch-related-products
    builder.addCase(fetchRelatedProduct.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchRelatedProduct.fulfilled, (state, action) => {
      const { data, success } = action?.payload ?? {};
      state.products = (data ?? [])
        .filter((product) => product.id !== state.productDetail?.id) // Exclude matching product
        .map((product) => ({
          ...product,
          cartQuanity: 1, // Adding a default key with value 1
        }));
      state.isLoading = false;
    });
    builder.addCase(fetchRelatedProduct.rejected, (state, action) => {
      state.isLoading = false;
    });
    // add-to-cart
    builder.addCase(addToCart.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(addToCart.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(addToCart.rejected, (state, action) => {
      state.isLoading = false;
    });
    // send-product-feedback
    builder.addCase(sendProductFeedback.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(sendProductFeedback.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(sendProductFeedback.rejected, (state, action) => {
      state.isLoading = false;
    });
    // fetch-card-details
    builder.addCase(fetchCartDetails.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchCartDetails.fulfilled, (state, action) => {
      const { data, success } = action?.payload ?? {};
      state.cartList = data ?? [];
      state.isLoading = false;
    });
    builder.addCase(fetchCartDetails.rejected, (state, action) => {
      state.isLoading = false;
    });
    // update-card-details
    builder.addCase(updateCartDetail.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(updateCartDetail.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(updateCartDetail.rejected, (state, action) => {
      state.isLoading = false;
    });
    // delete-porduct
    builder.addCase(deleteProduct.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(deleteProduct.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(deleteProduct.rejected, (state, action) => {
      state.isLoading = false;
    });
    // place-order
    builder.addCase(placeOrder.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(placeOrder.fulfilled, (state, action) => {
      const { transaction_unique_id } = action?.payload;
      pipSaveTransictionId(transaction_unique_id);
      state.isLoading = false;
    });
    builder.addCase(placeOrder.rejected, (state, action) => {
      state.isLoading = false;
    });
    // updaste-order-status
    builder.addCase(updateOrderStatus.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(updateOrderStatus.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(updateOrderStatus.rejected, (state, action) => {
      state.isLoading = false;
    });
  },
});

export const {
  increaseQuantity,
  decreaseQuantity,
  setSelectedProduct,
  increaseProductQuantity,
  decreaseProductQuantity,
} = orderSlice.actions;
export default orderSlice.reducer;
